import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'components/Spinner';
import { COMPANIES_API, USERS_API } from 'api';
import { availableLanguages } from 'utils/i18n';
import { USER_ROLES } from 'utils/constant';
import jsonAppData from 'utils/jsonAppData';
import { mapLink, telFmt, telLink } from 'utils';

const ConfigurationBlockCellProfilePopup = ({ uid }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [userProfile, setUserProfile] = useState(null);
	const [companyProfile, setCompanyProfile] = useState(null);
	const loadRequested = useRef(false);
	const { t } = useTranslation('baseFormPlaceholders');
	const isMounted = useRef(true);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const loadProfile = async () => {
		USERS_API.getUser(uid)
			.then(userDoc => {
				setUserProfile(userDoc);
				COMPANIES_API.getCompany(userDoc.companyId)
					.then(companyDoc => {
						setCompanyProfile(companyDoc);
					})
					.catch(e => {
						// eslint-disable-next-line no-console
						console.warn(`Error fetching company: ${e}`);
					})
					.finally(() => setIsLoading(false));
			})
			.catch(e => {
				// eslint-disable-next-line no-console
				console.warn(`Error fetching user: ${e}`);
				setIsLoading(false);
			});
	};

	if (!loadRequested.current && isMounted) {
		loadRequested.current = true;
		loadProfile();
	}

	const profileInfo = {};
	if (userProfile) {
		if (companyProfile) {
			profileInfo.company = `${companyProfile.name} (Ref: ${companyProfile.customerReference})`;
			profileInfo.companyAddress = `${companyProfile.address}, ${companyProfile.zip} ${companyProfile.city}`;
		} else {
			profileInfo.company = '...';
		}
		profileInfo.gender = userProfile.gender?.charAt(0) || '-';
		profileInfo.displayName = `${userProfile.name} ${userProfile.lastName}`;
		profileInfo.language = availableLanguages[userProfile.language];
		const functionFieldData = jsonAppData.registrationNewUserForm.find(
			o => o.id === 'userFunction'
		);
		profileInfo.function = t(
			functionFieldData.options.find(o => o.value === userProfile.role)?.label
		);
		profileInfo.isCompanyAdmin = userProfile.role === USER_ROLES.companyAdmin;
	}

	return (
		<div className="configuration_block__profile_popup" role="dialog">
			{isLoading ? (
				<Spinner />
			) : userProfile ? (
				<ul className="configuration_block__user_profile">
					<li>
						<span className="name">{profileInfo.displayName}</span>
						<span className="lang" title={profileInfo.language.nativeText}>
							{profileInfo.language.flag}
						</span>
						<span className="gender">({profileInfo.gender})</span>
					</li>
					<li>{profileInfo.function}</li>
					<li>
						<a href={`mailto:${userProfile.email}`}>{userProfile.email}</a>
					</li>
					{companyProfile && (
						<>
							<li>
								{profileInfo.company}
								{userProfile.isCompanyAdmin && '(admin)'}
							</li>
							<li>
								<a href={mapLink(profileInfo.companyAddress)}>{profileInfo.companyAddress}</a>
							</li>
							<li>
								<a href={telLink(companyProfile.tel)}>{telFmt(companyProfile.tel)}</a>
							</li>
						</>
					)}
				</ul>
			) : (
				<>Failed to load profile</>
			)}
		</div>
	);
};

export default ConfigurationBlockCellProfilePopup;
